import React, { useRef, useState } from 'react'
import {
  Note,
  TextArea,
  CodeArea,
  Tokens,
  Token,
  Problem,
  MessagePreview,
  FieldError,
  PlaceholderList,
  Placeholder,
  ModeSwitch,
  Column,
  tokenInserter,
} from './editorParts'
import {
  CONFIRMATION_TOKENS,
  compile,
  parse,
  preview,
} from './confirmationTemplate'

// The same message in one box, the way the link message reads one situation:
// plain text with names in brackets, and the patient's text under it.
const ConfirmationMessageEditor = ({ value, onChange, error }) => {
  const [text, setText] = useState(() => parse(value))
  const [mode, setMode] = useState(() =>
    parse(value) === null ? 'code' : 'simple'
  )
  const [cannotSimplify, setCannotSimplify] = useState(false)
  const box = useRef(null)

  const write = next => {
    setText(next)
    onChange(compile(next))
  }

  const insertToken = tokenInserter(box, text ?? '', write)

  const showSimple = () => {
    const parsed = parse(value)
    if (parsed === null) {
      setCannotSimplify(true)
      return
    }
    setText(parsed)
    setCannotSimplify(false)
    setMode('simple')
  }

  if (mode === 'code') {
    return (
      <Column>
        <Note>
          The message as a Liquid template. Only these placeholders exist;
          anything else is refused when you save.
        </Note>
        <CodeArea
          aria-label="Confirmation template"
          rows={4}
          spellCheck={false}
          $invalid={Boolean(error)}
          value={value}
          onChange={event => onChange(event.target.value)}
        />
        <PlaceholderList>
          {CONFIRMATION_TOKENS.map(({ variable }) => (
            <Placeholder key={variable}>{`{{${variable}}}`}</Placeholder>
          ))}
        </PlaceholderList>
        {error && <FieldError>{error}</FieldError>}
        <ModeSwitch type="button" onClick={showSimple}>
          Edit as plain text
        </ModeSwitch>
        {cannotSimplify && (
          <Problem $blocking>
            This message has logic of its own, so it can only be edited as code.
          </Problem>
        )}
      </Column>
    )
  }

  return (
    <Column>
      <TextArea
        aria-label="Confirmation message"
        ref={box}
        rows={3}
        $invalid={Boolean(error)}
        value={text}
        onChange={event => write(event.target.value)}
      />
      <Tokens>
        Add:
        {CONFIRMATION_TOKENS.map(({ label }) => (
          <Token key={label} type="button" onClick={() => insertToken(label)}>
            {label}
          </Token>
        ))}
      </Tokens>
      {error ? (
        <FieldError>{error}</FieldError>
      ) : (
        !text.trim() && <Problem $blocking>Write a message here.</Problem>
      )}

      <MessagePreview message={preview(text)} />

      <ModeSwitch type="button" onClick={() => setMode('code')}>
        Edit as code
      </ModeSwitch>
    </Column>
  )
}

export default ConfirmationMessageEditor
